import {
  createContext, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from "react";
import { Loader2, Search, X, type LucideIcon } from "lucide-react";
import "./ui.css";

type Variant = "primary" | "secondary" | "ghost" | "danger";

export function Button({ variant = "secondary", size, loading, icon: Icon, children, className = "", ...rest }:
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm"; loading?: boolean; icon?: LucideIcon }) {
  const iconOnly = !children;
  const cls = ["btn", `btn-${variant}`, size === "sm" && "btn-sm", iconOnly && "btn-icon", className].filter(Boolean).join(" ");
  const iconSize = size === "sm" ? 14 : 16;
  return (
    <button type="button" className={cls} disabled={rest.disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <Loader2 size={iconSize} className="spin" aria-hidden /> : Icon && <Icon size={iconSize} aria-hidden />}
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: ReactNode; error?: string; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? <span className="field-error" role="alert">{error}</span> : hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

type WithField = { label?: string; hint?: ReactNode; error?: string };

export function Input({ label, hint, error, id, ...rest }: InputHTMLAttributes<HTMLInputElement> & WithField) {
  const auto = useId();
  const inputId = id ?? auto;
  const input = <input id={inputId} className="input" aria-invalid={error ? true : undefined} {...rest} />;
  return label ? <Field label={label} hint={hint} error={error} htmlFor={inputId}>{input}</Field> : input;
}

export function Textarea({ label, hint, error, id, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & WithField) {
  const auto = useId();
  const inputId = id ?? auto;
  const el = <textarea id={inputId} className="textarea" aria-invalid={error ? true : undefined} {...rest} />;
  return label ? <Field label={label} hint={hint} error={error} htmlFor={inputId}>{el}</Field> : el;
}

export function Select({ label, hint, error, id, options, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & WithField & { options: { value: string; label: string }[] }) {
  const auto = useId();
  const inputId = id ?? auto;
  const el = (
    <select id={inputId} className="select" {...rest}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
  return label ? <Field label={label} hint={hint} error={error} htmlFor={inputId}>{el}</Field> : el;
}

export function SearchInput({ value, onChange, placeholder, autoFocus }: { value: string; onChange: (v: string) => void; placeholder: string; autoFocus?: boolean }) {
  return (
    <div className="search-wrap">
      <Search size={15} aria-hidden />
      <input className="input input-search" type="search" value={value} placeholder={placeholder} aria-label={placeholder}
        autoFocus={autoFocus} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <label className="switch" title={label}>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} aria-label={label} onChange={(e) => onChange(e.target.checked)} />
      <span />
    </label>
  );
}

export type Tone = "neutral" | "primary" | "secondary" | "success" | "warning" | "danger" | "info";

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={tone === "neutral" ? "badge" : `badge badge-${tone}`}>{children}</span>;
}

export function Card({ title, actions, children, pad = true }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; pad?: boolean }) {
  return (
    <section className="card">
      {title && <div className="card-header"><h3 className="grow">{title}</h3>{actions}</div>}
      <div className={pad ? "card-pad" : undefined}>{children}</div>
    </section>
  );
}

// Dialog uses the native <dialog> element: focus trapping, Escape to close and inert background
// come from the browser rather than being re-implemented.
export function Dialog({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  // A generated id: aria-labelledby is a space-separated id list, so a title-based id with spaces
  // left every dialog without a name for screen readers.
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className={wide ? "dialog dialog-wide" : "dialog"} onClose={onClose} aria-labelledby={titleId}>
      {open && (
        <>
          <div className="dialog-head row">
            <h2 id={titleId} className="grow">{title}</h2>
            <Button variant="ghost" size="sm" icon={X} aria-label="Close" onClick={onClose} />
          </div>
          <div className="dialog-body">{children}</div>
          {footer && <div className="dialog-foot">{footer}</div>}
        </>
      )}
    </dialog>
  );
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { value: T; label: ReactNode }[] }) {
  return (
    <div className="tabs" role="tablist">
      {items.map((i) => (
        <button key={i.value} role="tab" className="tab" aria-selected={i.value === value} onClick={() => onChange(i.value)}>{i.label}</button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, items, label }: { value: T; onChange: (v: T) => void; items: { value: T; label: ReactNode }[]; label: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {items.map((i) => (
        <button key={i.value} type="button" aria-pressed={i.value === value} onClick={() => onChange(i.value)}>{i.label}</button>
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, children, action }: { icon: LucideIcon; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <Icon size={32} aria-hidden />
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Skeleton({ width = "100%", height = 14, style }: { width?: number | string; height?: number; style?: React.CSSProperties }) {
  return <div className="skeleton" style={{ width, height, ...style }} aria-hidden />;
}

export function SkeletonRows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="stack" style={{ padding: 16 }} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} height={16} width={`${90 - (i % 3) * 15}%`} />)}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div className="empty" role="alert">
      <h3>Couldn't load this</h3>
      <p>{message}</p>
      {onRetry && <Button onClick={onRetry}>Try again</Button>}
    </div>
  );
}

export function Avatar({ name, size = 28, src }: { name: string; size?: number; src?: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.4 }} aria-hidden>
      {src ? <img src={src} alt="" width={size} height={size} /> : initials}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div className="grow">
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </header>
  );
}

type ToastItem = { id: number; message: string; tone: "default" | "error" };
const ToastContext = createContext<(message: string, tone?: ToastItem["tone"]) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((message: string, tone: ToastItem["tone"] = "default") => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, message, tone }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), tone === "error" ? 7000 : 4000);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => <div key={t.id} className={t.tone === "error" ? "toast toast-error" : "toast"}>{t.message}</div>)}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
